import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "./useAuth";
import { useSpaces } from "../context/SpaceProvider";
import { EXPERT_ROLE, VENDOR_ROLE } from "../lib/roles";
import { getUserType, isStudioUser, type UserType } from "../lib/userType";
import {
  useOrgAdminStatus,
  useMentorCollectionsQuery,
} from "./usePortalData";
import useRetryFetch from "./useRetryFetch";
import { config } from "../Environment";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  id: string;
  icon: LucideIcon;
  label: string;
  show: boolean;
  onClick: () => void;
  badge?: number;
}

export interface UseNavigationItemsReturn {
  isLoggedIn: boolean;
  isMentor: boolean;
  isVendor: boolean;
  userType: UserType | null;
  isStudio: boolean;
  isOrgAdmin: boolean;
  showGroups: boolean;
  activeItem: string;
  portalActiveTab: string;
  unreadChatsCount: number;
  navigateToPortalTab: (tab: string) => void;
  // Space-related
  spaces: ReturnType<typeof useSpaces>["spaces"];
  hasSpaces: boolean;
  selectedSpace: ReturnType<typeof useSpaces>["activeSpace"];
  selectedSpaceId: string | null;
  setActiveSpace: ReturnType<typeof useSpaces>["setActiveSpace"];
  isOnSpaces: boolean;
  // Navigation helpers
  navigate: ReturnType<typeof useNavigate>;
  t: (key: string, defaultValue?: string) => string;
}

export function useNavigationItems(): UseNavigationItemsReturn {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { auth, user } = useAuth();
  const isLoggedIn = !!auth?.email && !!auth?.accessToken;
  const isMentor = (user?.roles ?? []).includes(EXPERT_ROLE);
  const isVendor = (user?.roles ?? []).includes(VENDOR_ROLE);
  // Audience segment: drives which navigation shell the user sees.
  // Pre-fork accounts are inferred from the expert role — no blocking chooser.
  const userType = getUserType(user);
  const isStudio = isLoggedIn && isStudioUser(user);

  const { spaces, setActiveSpace, hasSpaces } = useSpaces();

  // Track which space is locally selected in SpacesView
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setSelectedSpaceId((e as CustomEvent).detail);
    };
    window.addEventListener("spaces-selected-change", handler);
    return () => window.removeEventListener("spaces-selected-change", handler);
  }, []);

  // Clear selected space when navigating away from /spaces
  useEffect(() => {
    if (pathname !== "/spaces") {
      setSelectedSpaceId(null);
    }
  }, [pathname]);

  const selectedSpace = spaces.find((s) => s._id === selectedSpaceId) || null;
  const { isOrgAdmin } = useOrgAdminStatus();
  const { data: mentorCollectionsData } = useMentorCollectionsQuery(
    isMentor || isOrgAdmin,
  );
  const mentorCollectionCount =
    mentorCollectionsData?.collections?.length ?? null;

  const [portalActiveTab, setPortalActiveTab] = useState(
    () => localStorage.getItem("portal_active_tab") || "activities",
  );

  // Unread chats count for badge
  const retryFetch = useRetryFetch();
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchUnreadCount = async () => {
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/unread-chats-count`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ type: "general" }),
          }
        );
        if (response.ok) {
          const result = await response.json();
          setUnreadChatsCount(result.data.unreadChatsCount);
        }
      } catch (error) {
        // Silently fail — badge just won't show
      }
    };
    fetchUnreadCount();
  }, [isLoggedIn]);

  useEffect(() => {
    const handler = (e: Event) => {
      setPortalActiveTab((e as CustomEvent).detail);
    };
    window.addEventListener("portal-tab-change", handler);
    return () => window.removeEventListener("portal-tab-change", handler);
  }, []);

  const navigateToPortalTab = (tab: string) => {
    localStorage.setItem("portal_active_tab", tab);
    setPortalActiveTab(tab);
    if (pathname !== "/portal") {
      navigate("/portal");
    } else {
      window.dispatchEvent(
        new CustomEvent("portal-tab-change", { detail: tab }),
      );
    }
  };

  const getActiveItem = (): string => {
    if (pathname === "/home") return "home";
    if (pathname === "/chats") return "chats";
    if (pathname === "/portal") return `portal_${portalActiveTab}`;
    if (pathname === "/spaces") return "spaces";
    if (pathname.startsWith("/marketplace")) return "marketplace";
    if (pathname === "/examples") return "examples";
    if (pathname === "/science") return "science";
    if (pathname === "/insights") return "insights";
    if (pathname.startsWith("/mentoring")) return "mentoring";
    if (pathname.startsWith("/programs")) return "programs";
    if (pathname.startsWith("/pwf")) return "studio";
    if (pathname.startsWith("/meetings")) return "meetings";
    if (pathname.startsWith("/commerce")) return "store";
    if (pathname === "/plans") return "plans";
    return "";
  };

  const activeItem = getActiveItem();
  const isOnSpaces = pathname === "/spaces";

  const showGroups =
    (isMentor || isOrgAdmin) &&
    mentorCollectionCount !== null &&
    mentorCollectionCount > 0;

  return {
    isLoggedIn,
    isMentor,
    isVendor,
    userType,
    isStudio,
    isOrgAdmin,
    showGroups,
    activeItem,
    portalActiveTab,
    unreadChatsCount,
    navigateToPortalTab,
    spaces,
    hasSpaces,
    selectedSpace,
    selectedSpaceId,
    setActiveSpace,
    isOnSpaces,
    navigate,
    t,
  };
}
